import React, { useCallback, useEffect, useState } from 'react';
import { ShoppingCartIcon, SearchIcon, PlusIcon, CheckCircleIcon, ClockIcon, EditIcon, TrashIcon, UserIcon, PackageIcon, AlertTriangleIcon, XIcon, CheckIcon, BanIcon, TruckIcon, HistoryIcon, PrinterIcon, AlertCircleIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { supabaseAdmin } from '../lib/supabase';
import { Order, OrderLine, Product } from '../types';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { generateCotizacionPDF } from '../components/CotizacionPDF';
// Promotion type for local use
interface ActivePromo {
  productId: string;
  tipo: 'oferta' | 'impulso';
  descuentoPct: number;
  precioOferta: number;
  notas: string;
}
// ── Moved outside component to avoid TDZ error ──────────────────────────────
type HeaderForm = {
  numeroPedido: string;
  farmacia: string;
  vendedor: string;
  fecha: string;
  estado: Order['estado'];
};
const emptyHeader: HeaderForm = {
  numeroPedido: '',
  farmacia: '',
  vendedor: '',
  fecha: new Date().toISOString().split('T')[0],
  estado: 'pendiente'
};
const STATUS_CYCLE: Order['estado'][] = ['pendiente', 'procesando', 'empacado', 'enviado', 'entregado', 'cancelado'];
// ─────────────────────────────────────────────────────────────────────────────
export function PedidosPage() {
  const {
    orders,
    refreshData,
    updateOrderStatus,
    clients,
    users
  } = useApp();
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const isRepartidor = user?.permisos?.dashboard_entregas === true && !isAdmin;
  const isCliente = user?.rolNombre === 'Cliente';
  const canEditOrders = !isCliente && (user?.permisos?.pedidos_editar !== false || isAdmin);
  const [inventory, setInventory] = useState<Product[]>([]);
  const [activePromos, setActivePromos] = useState<ActivePromo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(isRepartidor ? 'empacado' : 'all');
  // Repartidor tab: 'pendientes' (empacado+enviado) | 'entregados' | 'todos'
  const [repartidorTab, setRepartidorTab] = useState<'pendientes' | 'entregados' | 'todos'>('pendientes');
  const [vendedorFilter, setVendedorFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [headerForm, setHeaderForm] = useState<HeaderForm>(emptyHeader);
  const [lineas, setLineas] = useState<OrderLine[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOrderId, setSavedOrderId] = useState<string | null>(null);
  // Track saved cotización to prevent duplicates across Print + Save
  const [savedCotizacionId, setSavedCotizacionId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  // Build dynamic lists from real data — only actual field vendedores
  const vendedores = users.filter(u => {
    if (!u.activo) return false;
    if (!u.permisos?.['new-visit']) return false;
    // Exclude admins and repartidores — they have new-visit but aren't vendedores
    if (u.rolNombre === 'Administrador') return false;
    if (u.permisos?.users === true) return false;
    if (u.permisos?.dashboard_entregas === true && u.rolNombre !== 'Vendedor') return false;
    return true;
  }).map(u => u.nombre);
  const farmacias = clients.map(c => c.nombre).sort();
  useEffect(() => {
    loadProducts();
    loadPromotions();
  }, []);
  const refreshProductData = useCallback(() => {
    loadProducts();
    loadPromotions();
  }, []);
  useRealtimeRefresh(['products', 'product_promotions'], refreshProductData);
  const mapProduct = (row: any): Product => ({
    id: row.id,
    nombre: row.nombre,
    sku: row.sku,
    stock: row.stock,
    precio: row.precio,
    subTotal: row.sub_total || 0,
    itbis: row.itbis || 0,
    proveedor: row.proveedor || '',
    estado: row.estado || 'activo',
    stockMinimo: row.stock_minimo ?? 5
  });
  const loadProducts = async () => {
    setIsLoading(true);
    try {
      // EMERGENCY: Single query with limit instead of while(hasMore) loop
      const {
        data,
        error
      } = await supabaseAdmin.from('products').select('*').order('nombre').limit(2000);
      if (!error && data) {
        setInventory(data.map(mapProduct));
      }
    } finally {
      setIsLoading(false);
    }
  };
  const loadPromotions = async () => {
    try {
      const {
        data
      } = await supabaseAdmin.from('product_promotions').select('product_id, tipo, descuento_pct, precio_oferta, notas').eq('activo', true);
      if (data) {
        setActivePromos(data.map((r: any) => ({
          productId: r.product_id,
          tipo: r.tipo,
          descuentoPct: r.descuento_pct || 0,
          precioOferta: r.precio_oferta || 0,
          notas: r.notas || ''
        })));
      }
    } catch {}
  };
  const updateStatus = async (order: Order, newStatus: Order['estado']) => {
    // Optimistic update — UI changes instantly
    await updateOrderStatus(order.id, newStatus);
    addActivityLog('Cambió estado pedido', `Pedido: ${order.numeroPedido} | ${order.estado} → ${newStatus} | Farmacia: ${order.farmacia}`);
    setSavedOrderId(order.id);
    setTimeout(() => setSavedOrderId(null), 1500);
    // If cancelling, restore stock in background (don't block UI)
    if (newStatus === 'cancelado' && order.estado !== 'cancelado' && order.lineas) {
      Promise.all((order.lineas || []).map(async l => {
        const {
          data: prod
        } = await supabaseAdmin.from('products').select('stock').eq('id', l.productoId).single();
        if (prod) {
          await supabaseAdmin.from('products').update({
            stock: prod.stock + l.cantidad
          }).eq('id', l.productoId);
        }
      })).then(() => loadProducts());
    }
  };
  const cycleStatus = async (order: Order) => {
    const currentIndex = STATUS_CYCLE.indexOf(order.estado);
    const nextStatus = STATUS_CYCLE[(currentIndex + 1) % STATUS_CYCLE.length];
    updateStatus(order, nextStatus);
  };
  // Vendedores de campo (con permiso new-visit) solo ven sus propios pedidos
  // Almacén y otros roles con permiso pedidos ven todos los pedidos
  const isFieldVendor = !isAdmin && !isRepartidor && !isCliente && user?.permisos?.['new-visit'] === true;
  // Repartidor global counts (from ALL orders, before filtering)
  const repartidorPendientesCount = orders.filter(o => o.estado === 'empacado' || o.estado === 'enviado').length;
  const repartidorEntregadosCount = orders.filter(o => o.estado === 'entregado').length;
  const filteredOrders = orders.filter(order => {
    // Cliente only sees their own orders (matched by farmacia = user nombre)
    if (isCliente && order.farmacia !== user?.nombre) return false;
    if (isRepartidor) {
      // Repartidor can see empacado, enviado, AND entregado orders
      if (order.estado !== 'empacado' && order.estado !== 'enviado' && order.estado !== 'entregado') return false;
      // Apply repartidor tab filter
      if (repartidorTab === 'pendientes' && order.estado !== 'empacado' && order.estado !== 'enviado') return false;
      if (repartidorTab === 'entregados' && order.estado !== 'entregado') return false;
      // Apply search filter
      const matchesSearch = searchQuery.trim() === '' || order.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || order.numeroPedido.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    }
    if (isFieldVendor && order.vendedor !== user?.nombre) return false;
    const matchesSearch = order.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || order.numeroPedido.toLowerCase().includes(searchQuery.toLowerCase()) || order.vendedor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.estado === statusFilter;
    const matchesVendedor = vendedorFilter === 'all' || order.vendedor === vendedorFilter;
    return matchesSearch && matchesStatus && matchesVendedor;
  });
  // Sorting setup
  const {
    items: sortedOrders,
    requestSort,
    sortConfig
  } = useSortableData(filteredOrders, {
    numeroPedido: o => o.numeroPedido,
    farmacia: o => o.farmacia,
    vendedor: o => o.vendedor,
    fecha: o => new Date(o.fecha).getTime(),
    items: o => o.items,
    total: o => o.total,
    estado: o => o.estado
  });
  // Default sort by date descending if no sort config
  const finalOrders = sortConfig ? sortedOrders : [...sortedOrders].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalPedidos = finalOrders.length;
  const pedidosPendientes = finalOrders.filter(o => o.estado === 'pendiente' || o.estado === 'procesando').length;
  const pedidosCompletados = finalOrders.filter(o => o.estado === 'entregado').length;
  const montoTotal = finalOrders.reduce((sum, o) => sum + o.total, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const getStatusBadge = (status: Order['estado']) => {
    switch (status) {
      case 'entregado':
        return <Badge variant="success" data-id="element-3752" data-mp="1dzxsyd9m4kk7">Entregado</Badge>;
      case 'enviado':
        return <Badge variant="info" data-id="element-3753" data-mp="1e220r6amu3pr">Enviado</Badge>;
      case 'empacado':
        return <Badge variant="info" data-id="element-3754" data-mp="3povteui1urbv">Empacado</Badge>;
      case 'procesando':
        return <Badge variant="warning" data-id="element-3755" data-mp="3tpfqxz6jbwyj">Procesando</Badge>;
      case 'cancelado':
        return <Badge variant="danger" data-id="element-3756" data-mp="2i369a6g11dv">Cancelado</Badge>;
      default:
        return <Badge variant="default" data-id="element-3757" data-mp="791tyutrh1r5">Pendiente</Badge>;
    }
  };
  // Product search results for adding to order
  const productResults = (() => {
    // Filter out products already added to the order
    const available = inventory.filter(p => (!p.estado || p.estado === 'activo') && !lineas.some(l => l.productoId === p.id));
    let results: Product[];
    // If search is empty, show first 20 products when modal is open
    if (productSearch.trim().length === 0) {
      results = isModalOpen ? available : [];
    } else {
      // Filter by search term
      results = available.filter(p => p.nombre.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase()));
    }
    // Sort: promoted products first, then alphabetical
    return results.sort((a, b) => {
      const aPromo = activePromos.find(pr => pr.productId === a.id);
      const bPromo = activePromos.find(pr => pr.productId === b.id);
      if (aPromo && !bPromo) return -1;
      if (!aPromo && bPromo) return 1;
      return a.nombre.localeCompare(b.nombre);
    }).slice(0, 20);
  })();
  const addProductToOrder = (product: Product) => {
    // Apply promotion discount to price if available
    const promo = activePromos.find(pr => pr.productId === product.id);
    let finalPrice = product.precio;
    if (promo) {
      if (promo.precioOferta > 0) {
        finalPrice = promo.precioOferta;
      } else if (promo.descuentoPct > 0) {
        finalPrice = Math.round(finalPrice * (1 - promo.descuentoPct / 100) * 100) / 100;
      }
    }
    setLineas(prev => {
      const existing = prev.find(l => l.productoId === product.id);
      if (existing) {
        return prev.map(l => l.productoId === product.id ? {
          ...l,
          cantidad: l.cantidad + 1
        } : l);
      }
      return [...prev, {
        productoId: product.id,
        nombre: product.nombre,
        sku: product.sku,
        cantidad: 1,
        precio: finalPrice,
        itbis: product.itbis || 0,
        stockDisponible: product.stock
      }];
    });
    setProductSearch('');
  };
  const updateLineCantidad = (productoId: string, cantidad: number) => {
    // Allow empty/0 without removing — only the × button removes products
    const safeCantidad = isNaN(cantidad) || cantidad < 0 ? 0 : cantidad;
    setLineas(prev => prev.map(l => l.productoId === productoId ? {
      ...l,
      cantidad: safeCantidad
    } : l));
  };
  const removeLinea = (productoId: string) => {
    setLineas(prev => prev.filter(l => l.productoId !== productoId));
  };
  const orderSubtotal = lineas.reduce((sum, l) => sum + l.cantidad * l.precio, 0);
  const orderItbisTotal = lineas.reduce((sum, l) => sum + l.cantidad * (l.itbis || 0), 0);
  const orderTotal = orderSubtotal + orderItbisTotal;
  const hasStockIssues = lineas.some(l => l.cantidad > l.stockDisponible);
  const totalPages = Math.ceil(finalOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = finalOrders.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const openAddModal = () => {
    setEditingOrder(null);
    setSaveError(null);
    setSavedCotizacionId(null);
    const currentYear = new Date().getFullYear();
    const yearPrefix = `PED-${currentYear}-`;
    const existing = orders.map(o => {
      const match = o.numeroPedido.match(new RegExp(`^PED-${currentYear}-(\\d+)$`));
      return match ? parseInt(match[1], 10) : 0;
    }).filter(n => n > 0);
    const nextNum = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    const nextPedido = `${yearPrefix}${String(nextNum).padStart(3, '0')}`;
    setHeaderForm({
      ...emptyHeader,
      numeroPedido: nextPedido,
      farmacia: isCliente ? user?.nombre || '' : '',
      vendedor: isCliente ? user?.nombre || '' : ''
    });
    setLineas([]);
    setProductSearch('');
    setIsModalOpen(true);
  };
  const openEditModal = async (order: Order) => {
    setEditingOrder(order);
    setSaveError(null);
    setHeaderForm({
      numeroPedido: order.numeroPedido,
      farmacia: order.farmacia,
      vendedor: order.vendedor,
      fecha: new Date(order.fecha).toISOString().split('T')[0],
      estado: order.estado
    });
    setProductSearch('');
    setIsModalOpen(true);
    // Use order.lineas if available, otherwise fetch from DB
    let orderLineas: OrderLine[] = order.lineas && order.lineas.length > 0 ? [...order.lineas] : [];
    if (orderLineas.length === 0) {
      // Fetch order_lines directly from Supabase
      try {
        const {
          data
        } = await supabaseAdmin.from('order_lines').select('*').eq('order_id', order.id);
        if (data && data.length > 0) {
          orderLineas = data.map((l: any): OrderLine => ({
            productoId: l.producto_id,
            nombre: l.nombre,
            sku: l.sku,
            cantidad: l.cantidad,
            precio: l.precio,
            itbis: l.itbis || 0,
            stockDisponible: l.stock_disponible
          }));
        }
      } catch (err) {
        console.error('Error fetching order_lines:', err);
      }
    }
    // Update stockDisponible from current inventory for accurate indicators
    const updatedLineas = orderLineas.map(l => {
      const currentProduct = inventory.find(p => p.id === l.productoId);
      return {
        ...l,
        stockDisponible: currentProduct ? currentProduct.stock + l.cantidad : l.stockDisponible
      };
    });
    setLineas(updatedLineas);
  };
  const handleSave = async () => {
    if (!headerForm.numeroPedido || lineas.length === 0) return;
    if (!headerForm.farmacia) {
      setSaveError('Debes seleccionar una farmacia.');
      return;
    }
    if (!headerForm.vendedor && !isCliente) {
      setSaveError('Debes seleccionar un vendedor.');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const totalItems = lineas.reduce((sum, l) => sum + l.cantidad, 0);
      const orderPayload = {
        numero_pedido: headerForm.numeroPedido,
        farmacia: headerForm.farmacia,
        vendedor: headerForm.vendedor,
        fecha: new Date(headerForm.fecha).toISOString(),
        items: totalItems,
        total: orderTotal,
        estado: headerForm.estado
      };
      let orderLinesOk = true;
      let cotizacionOk = true;
      if (editingOrder) {
        const {
          error: updateError
        } = await supabaseAdmin.from('orders').update(orderPayload).eq('id', editingOrder.id);
        if (updateError) {
          console.error('Error actualizando pedido:', updateError);
          setSaveError('Error al actualizar el pedido: ' + updateError.message);
          return;
        }
        const {
          error: deleteError
        } = await supabaseAdmin.from('order_lines').delete().eq('order_id', editingOrder.id);
        if (deleteError) {
          console.error('Error eliminando líneas:', deleteError.message, deleteError.details, deleteError.hint);
          setSaveError('Error al actualizar las líneas del pedido: ' + deleteError.message + (deleteError.hint ? ' — Hint: ' + deleteError.hint : '') + '. Ejecuta la migración SQL: supabase/cotizaciones_order_lines_fix.sql');
          return;
        }
        if (lineas.length > 0) {
          const editLinesPayload = lineas.map(l => ({
            order_id: editingOrder.id,
            producto_id: l.productoId,
            nombre: l.nombre,
            sku: l.sku,
            cantidad: l.cantidad,
            precio: l.precio,
            stock_disponible: l.stockDisponible
          }));
          console.log('[DEBUG] Insertando order_lines (edit):', JSON.stringify(editLinesPayload));
          const {
            data: editInsertedLines,
            error: linesError
          } = await supabaseAdmin.from('order_lines').insert(editLinesPayload).select();
          if (linesError) {
            orderLinesOk = false;
            console.error('Error insertando líneas:', linesError.message, linesError.details, linesError.hint);
            setSaveError('Error al guardar las líneas del pedido (order_lines): ' + linesError.message + (linesError.hint ? ' — Hint: ' + linesError.hint : '') + '. Ejecuta la migración SQL: supabase/cotizaciones_order_lines_fix.sql');
            // Don't return — the order was updated, just lines failed
          } else {
            console.log('[DEBUG] order_lines (edit) insertadas OK:', editInsertedLines?.length, 'filas');
          }
        }
      } else {
        const {
          data: newOrder,
          error: insertError
        } = await supabaseAdmin.from('orders').insert(orderPayload).select().single();
        if (insertError || !newOrder) {
          console.error('Error creando pedido:', insertError);
          setSaveError('Error al crear el pedido: ' + (insertError?.message || 'No se recibió respuesta del servidor'));
          return;
        }
        if (lineas.length > 0) {
          const orderLinesPayload = lineas.map(l => ({
            order_id: newOrder.id,
            producto_id: l.productoId,
            nombre: l.nombre,
            sku: l.sku,
            cantidad: l.cantidad,
            precio: l.precio,
            stock_disponible: l.stockDisponible
          }));
          console.log('[DEBUG] Insertando order_lines:', JSON.stringify(orderLinesPayload));
          const {
            data: insertedLines,
            error: linesError
          } = await supabaseAdmin.from('order_lines').insert(orderLinesPayload).select();
          if (linesError) {
            orderLinesOk = false;
            console.error('Error insertando líneas del nuevo pedido:', linesError.message, linesError.details, linesError.hint);
            setSaveError('El pedido se creó pero hubo un error al guardar los productos (order_lines): ' + linesError.message + (linesError.hint ? ' — Hint: ' + linesError.hint : '') + '. Ejecuta la migración SQL: supabase/cotizaciones_order_lines_fix.sql');
            // Don't return — the order was created, just lines failed
          } else {
            console.log('[DEBUG] order_lines insertadas OK:', insertedLines?.length, 'filas');
          }
          // ── Reduce stock for each product in the new order ──────────────
          await Promise.all(lineas.map(async l => {
            const newStock = Math.max(0, l.stockDisponible - l.cantidad);
            await supabaseAdmin.from('products').update({
              stock: newStock
            }).eq('id', l.productoId);
          }));
          // Refresh local inventory so stock indicators update immediately
          await loadProducts();
        }
        // ── Guardar cotización automáticamente al crear pedido (skip if already saved by Print) ─────────
        if (!savedCotizacionId) {
          try {
            const totalItems = lineas.reduce((sum, l) => sum + l.cantidad, 0);
            const cotizacionPayload = {
              numero_cotizacion: headerForm.numeroPedido,
              farmacia: headerForm.farmacia,
              vendedor: headerForm.vendedor,
              fecha: new Date(headerForm.fecha).toISOString(),
              items: totalItems,
              total: orderTotal
            };
            const {
              data: newCotizacion,
              error: cotError
            } = await supabaseAdmin.from('cotizaciones').insert(cotizacionPayload).select().single();
            if (cotError) {
              console.error('Error guardando cotización al crear pedido:', cotError.message);
              cotizacionOk = false;
            }
            if (newCotizacion) {
              setSavedCotizacionId(newCotizacion.id);
              if (lineas.length > 0) {
                const cotLinesPayload = lineas.map(l => ({
                  cotizacion_id: newCotizacion.id,
                  producto_id: l.productoId,
                  nombre: l.nombre,
                  sku: l.sku,
                  cantidad: l.cantidad,
                  precio: l.precio,
                  stock_disponible: l.stockDisponible
                }));
                const {
                  error: cotLinesError
                } = await supabaseAdmin.from('cotizacion_lines').insert(cotLinesPayload).select();
                if (cotLinesError) {
                  console.error('Error guardando líneas de cotización:', cotLinesError.message);
                  cotizacionOk = false;
                }
              }
            }
          } catch (cotErr: any) {
            console.error('Error al guardar cotización:', cotErr);
            cotizacionOk = false;
          }
        }
      }
      await refreshData();
      // Only close modal and clear error if everything saved successfully
      if (orderLinesOk && cotizacionOk) {
        addActivityLog(editingOrder ? 'Editó pedido' : 'Creó pedido', `Pedido: ${headerForm.numeroPedido} | Farmacia: ${headerForm.farmacia} | Total: RD${orderTotal.toFixed(2)}`);
        setIsModalOpen(false);
        setSaveError(null);
      }
      // If there's a saveError (from cotizacion or order_lines), keep modal open
    } catch (err: any) {
      console.error('Error inesperado guardando pedido:', err);
      setSaveError('Error inesperado: ' + (err?.message || 'Intente nuevamente'));
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingOrder) return;
    // Restore stock if order was not cancelled/delivered
    if (editingOrder.estado !== 'cancelado' && editingOrder.lineas) {
      await Promise.all((editingOrder.lineas || []).map(async l => {
        const {
          data: prod
        } = await supabaseAdmin.from('products').select('stock').eq('id', l.productoId).single();
        if (prod) {
          await supabaseAdmin.from('products').update({
            stock: prod.stock + l.cantidad
          }).eq('id', l.productoId);
        }
      }));
    }
    await supabaseAdmin.from('orders').delete().eq('id', editingOrder.id);
    addActivityLog('Eliminó pedido', `Pedido: ${editingOrder.numeroPedido} | Farmacia: ${editingOrder.farmacia}`);
    await refreshData();
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  const getStockIndicator = (linea: OrderLine) => {
    const stockRestante = linea.stockDisponible - linea.cantidad;
    if (linea.stockDisponible === 0) return {
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      label: 'Sin stock',
      icon: '✗'
    };
    if (linea.cantidad > linea.stockDisponible) return {
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      label: `Faltan ${linea.cantidad - linea.stockDisponible} uds`,
      icon: '✗'
    };
    if (stockRestante === 0) return {
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      label: 'Quedarán 0 uds',
      icon: '⚠'
    };
    if (stockRestante <= linea.stockDisponible * 0.2) return {
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      label: `Quedarán ${stockRestante} uds`,
      icon: '⚠'
    };
    return {
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      label: `Quedarán ${stockRestante} uds`,
      icon: '✓'
    };
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-3758" data-mp="6m7kqrw5pu4r">
      <PageHeader title={isRepartidor ? 'Entregas' : isCliente ? 'Mis Pedidos' : 'Pedidos'} subtitle={isRepartidor ? 'Pedidos empacados listos para entregar' : isCliente ? 'Tus órdenes de compra' : 'Gestión y seguimiento de órdenes de compra'} icon={isRepartidor ? <TruckIcon className="w-6 h-6" data-id="element-3760" data-mp="2jtgwkbjei4q0" /> : <ShoppingCartIcon className="w-6 h-6" data-id="element-3761" data-mp="2jsxcopa6x69c" />} data-id="element-3759" data-mp="205hnr68ed7n8">
        {!isRepartidor && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3763" data-mp="2su54qpm45auz" />} onClick={openAddModal} data-id="element-3762" data-mp="2nwvcz3b94n3z">
            Nuevo Pedido
          </Button>}
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-3764" data-mp="yfud69e4w2li">
        <StatsCard title={isRepartidor ? 'Por Entregar' : 'Total Pedidos'} value={totalPedidos} icon={isRepartidor ? <PackageIcon className="w-5 h-5" data-id="element-3766" data-mp="vw7t3nyqdalm" /> : <ShoppingCartIcon className="w-5 h-5" data-id="element-3767" data-mp="r467vcjl49jg" />} variant={isRepartidor ? 'warning' : 'primary'} data-id="element-3765" data-mp="yhyrzmdkmm0k" />
        <StatsCard title={isRepartidor ? 'Entregados Hoy' : 'En Proceso'} value={isRepartidor ? orders.filter(o => o.estado === 'entregado' && new Date(o.fecha).toDateString() === new Date().toDateString()).length : pedidosPendientes} icon={isRepartidor ? <CheckCircleIcon className="w-5 h-5" data-id="element-3769" data-mp="zxj8l0nw4fg3" /> : <ClockIcon className="w-5 h-5" data-id="element-3770" data-mp="zzdtiez5w7k6" />} variant={isRepartidor ? 'success' : 'warning'} data-id="element-3768" data-mp="m64oeqi8y87g" />
        <StatsCard title="Entregados" value={isRepartidor ? orders.filter(o => o.estado === 'entregado').length : pedidosCompletados} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-3772" data-mp="2dxkj9x15pgmx" />} variant="success" data-id="element-3771" data-mp="3kmz76htcp8ga" />
        <StatsCard title="Monto Total" value={formatCurrency(montoTotal)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-3774" data-mp="2nufea5y80ywg" />} variant="default" data-id="element-3773" data-mp="2slnpn7j42uc9" />
      </div>

      {/* Repartidor Tab Filter */}
      {isRepartidor && <div className="flex items-center gap-1.5 sm:gap-2 bg-white border border-slate-200 rounded-xl p-1 sm:p-1.5 animate-stagger-1 overflow-x-auto" data-id="element-3775" data-mp="2wn0y7pdl9bmv">
          <button onClick={() => {
        setRepartidorTab('pendientes');
        setCurrentPage(1);
      }} className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${repartidorTab === 'pendientes' ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-3776" data-mp="3biyr8ys7tzuv">
            <PackageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-3777" data-mp="3nwlyd12uwhrr" />
            Por Entregar
            {repartidorPendientesCount > 0 && <span className={`ml-0.5 sm:ml-1 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${repartidorTab === 'pendientes' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'}`} data-id="element-3778" data-mp="3e57u6fzymtsa">
                {repartidorPendientesCount}
              </span>}
          </button>
          <button onClick={() => {
        setRepartidorTab('entregados');
        setCurrentPage(1);
      }} className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${repartidorTab === 'entregados' ? 'bg-emerald-500 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-3779" data-mp="396ecebdn4gmb">
            <CheckCircleIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-3780" data-mp="ybh7m1npha5a" />
            Entregados
            {repartidorEntregadosCount > 0 && <span className={`ml-0.5 sm:ml-1 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${repartidorTab === 'entregados' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-3781" data-mp="rws3erpptgfw">
                {repartidorEntregadosCount}
              </span>}
          </button>
          <button onClick={() => {
        setRepartidorTab('todos');
        setCurrentPage(1);
      }} className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${repartidorTab === 'todos' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-3782" data-mp="1fmcr1nwtd1vi">
            <HistoryIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-3783" data-mp="1yfgn1fxi7qta" />
            Todos
          </button>
        </div>}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 animate-stagger-2" data-id="element-3784" data-mp="21vt6syi1yt4w">
        <div className="relative flex-1" data-id="element-3785" data-mp="25wmxfzmvqm1h">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3786" data-mp="25xoauardbkmd" />
          <input type="text" placeholder={isRepartidor ? 'Buscar por farmacia o pedido...' : isCliente ? 'Buscar por número de pedido...' : 'Buscar por farmacia, pedido o vendedor...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-3787" data-mp="2av80s7xfhlu5" />
        </div>
        {!isRepartidor && !isCliente && <>
            <select value={vendedorFilter} onChange={e => setVendedorFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-3788" data-mp="38jrjfjwdd4ea">
              <option value="all" data-id="element-3789" data-mp="ival1us4fmhp">Todos los vendedores</option>
              {vendedores.map(v => <option key={v} value={v} data-id="element-3790" data-mp="dwpfdfu3oalv">
                  {v}
                </option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-3791" data-mp="3mdbvmboz3bbk">
              <option value="all" data-id="element-3792" data-mp="3raao6pszna7n">Todos los estados</option>
              <option value="pendiente" data-id="element-3793" data-mp="1amq6vmlut56v">Pendiente</option>
              <option value="procesando" data-id="element-3794" data-mp="1al597o48t64s">Procesando</option>
              <option value="empacado" data-id="element-3795" data-mp="16lo5az4gsh1q">Empacado</option>
              <option value="enviado" data-id="element-3796" data-mp="16n7i2fqww9yr">Enviado</option>
              <option value="entregado" data-id="element-3797" data-mp="1e3m8klw1yvwi">Entregado</option>
              <option value="cancelado" data-id="element-3798" data-mp="1e708lnh8ut0s">Cancelado</option>
            </select>
          </>}
      </div>

      {/* Orders list */}
      <Card padding="none" className="animate-stagger-3" data-id="element-3799" data-mp="1479idxzpv06i">
        {filteredOrders.length === 0 ? <div className="p-8 sm:p-16 text-center animate-fade-in" data-id="element-3800" data-mp="196m249vajr4v">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3801" data-mp="1wvf1yhf1aj4r">
              {isRepartidor ? <TruckIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-3802" data-mp="21tgjgxwtaae4" /> : <ShoppingCartIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-3803" data-mp="1n5279wdi99kw" />}
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-3804" data-mp="1s2c8e9a9b9ok">
              {isRepartidor ? repartidorTab === 'entregados' ? 'Sin entregas registradas' : repartidorTab === 'pendientes' ? 'Sin entregas pendientes' : 'Sin pedidos' : isCliente ? 'No hay pedidos para ti' : 'No hay pedidos'}
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-3805" data-mp="2legr961pmq0m">
              {isRepartidor ? repartidorTab === 'entregados' ? 'Aún no has marcado ningún pedido como entregado.' : repartidorTab === 'pendientes' ? 'No hay pedidos empacados listos para entregar.' : 'No se encontraron pedidos.' : isCliente ? 'No se encontraron pedidos para ti.' : 'No se encontraron pedidos con los filtros aplicados'}
            </p>
          </div> : <>
            {/* ── MOBILE CARD VIEW (< sm) ── */}
            <div className="sm:hidden divide-y divide-slate-100" data-id="element-3806" data-mp="1ns9m9v1huvnq">
              {paginatedOrders.map(order => <div key={order.id} className="p-3.5" data-id="element-3807" data-mp="3aqoyljsnoz4">
                  <div className="flex items-start justify-between gap-2" data-id="element-3808" data-mp="39z1xz49izkg">
                    <div className="min-w-0 flex-1" data-id="element-3809" data-mp="880lel5lp0wg">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-3810" data-mp="d8ocf6ttxhqo">
                        <p className="font-bold text-slate-900 text-sm" data-id="element-3811" data-mp="d6ahqz32a7jn">
                          {order.numeroPedido}
                        </p>
                        {isRepartidor ? getStatusBadge(order.estado) : <button onClick={() => cycleStatus(order)} className="cursor-pointer hover:opacity-75 transition-opacity" data-id="element-3812" data-mp="3loq5fcgdfwie">
                            {getStatusBadge(order.estado)}
                          </button>}
                      </div>
                      <p className="font-semibold text-slate-800 text-sm mt-1" data-id="element-3813" data-mp="31zpmq6038izh">
                        {order.farmacia}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500" data-id="element-3814" data-mp="1vd5af2i98ttq">
                        <span className="flex items-center gap-1" data-id="element-3815" data-mp="1qfn0b8si48k5">
                          <UserIcon className="w-3 h-3" data-id="element-3816" data-mp="25c49puh4rd42" />
                          {order.vendedor}
                        </span>
                        <span data-id="element-3817" data-mp="20aon3xzb0yjk">{formatDate(order.fecha)}</span>
                        <span data-id="element-3818" data-mp="2eazfj5g9il6u">{order.items} items</span>
                      </div>
                    </div>
                    <p className="text-base font-bold text-[#2952C4] flex-shrink-0" data-id="element-3819" data-mp="29bmwkvsihy9j">
                      {formatCurrency(order.total)}
                    </p>
                  </div>
                  {/* Action buttons */}
                  <div className="mt-2.5 flex items-center gap-1.5 justify-end" data-id="element-3820" data-mp="2j39e19vg0tkq">
                    {savedOrderId === order.id && <span className="text-xs text-emerald-600 font-medium animate-pulse mr-auto" data-id="element-3821" data-mp="2t0nfa28spbsg">
                        ✓ Guardado
                      </span>}
                    {isRepartidor ? <>
                        {(order.estado === 'empacado' || order.estado === 'enviado') && <>
                            <button onClick={() => updateStatus(order, 'entregado')} className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors" data-id="element-3822" data-mp="37n42adau6lo6">
                              <CheckIcon className="w-3.5 h-3.5" data-id="element-3823" data-mp="2jykv613iwexo" />
                              Entregado
                            </button>
                            <button onClick={() => updateStatus(order, 'cancelado')} className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-semibold rounded-lg transition-colors" data-id="element-3824" data-mp="2nzmz2v8dxf5z">
                              <BanIcon className="w-3.5 h-3.5" data-id="element-3825" data-mp="3az0lhnwxb716" />
                            </button>
                          </>}
                        <button onClick={() => openEditModal(order)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3826" data-mp="2w9b6opvwr4if">
                          <EditIcon className="w-4 h-4" data-id="element-3827" data-mp="2rb1bifa6o3wt" />
                        </button>
                      </> : isCliente ? null : <>
                        {order.estado === 'pendiente' && <>
                            <button onClick={() => updateStatus(order, 'procesando')} title="Aprobar" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" data-id="element-3828" data-mp="2icfj7sewtaiu">
                              <CheckIcon className="w-4 h-4" data-id="element-3829" data-mp="20yo3sndil46t" />
                            </button>
                            <button onClick={() => updateStatus(order, 'cancelado')} title="Cancelar" className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3830" data-mp="1w031miepcafq">
                              <BanIcon className="w-4 h-4" data-id="element-3831" data-mp="2jtbgu87oq87f" />
                            </button>
                          </>}
                        <button onClick={() => openEditModal(order)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3832" data-mp="2tomz30iaxxjx">
                          <EditIcon className="w-4 h-4" data-id="element-3833" data-mp="zdhxe1qrzv78" />
                        </button>
                        <button onClick={() => {
                  setEditingOrder(order);
                  setIsDeleteModalOpen(true);
                }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3834" data-mp="uen6ecinn0c1">
                          <TrashIcon className="w-4 h-4" data-id="element-3835" data-mp="1y1ififoely9f" />
                        </button>
                      </>}
                  </div>
                </div>)}
            </div>

            {/* ── DESKTOP TABLE (sm+) ── */}
            <div className="hidden sm:block overflow-x-auto" data-id="element-3836" data-mp="2brvngi1apcyr">
              <table className="w-full" data-id="element-3837" data-mp="2bsnls0ma1osq">
                <thead data-id="element-3838" data-mp="2gqp2wg057e1k">
                  <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-3839" data-mp="2gpx7p6agjibt">
                    <SortableTableHeader label="Pedido" sortKey="numeroPedido" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3840" data-mp="2lqbekl0bom89" />
                    <SortableTableHeader label="Farmacia" sortKey="farmacia" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3841" data-mp="a26m182kzb0b" />
                    <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3842" data-mp="xsk5ia73o1ja" />
                    <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3843" data-mp="j463g1hich3s" />
                    <SortableTableHeader label="Items" sortKey="items" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3844" data-mp="w9skg6f1mc41" />
                    <SortableTableHeader label="Total" sortKey="total" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3845" data-mp="rb7km83pdug4" />
                    <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3846" data-mp="1a9tkjjnhhfig" />
                    <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase font-heading" data-id="element-3847" data-mp="3nfhu0ri692va">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-3848" data-mp="10f9ebcwsnqm">
                  {paginatedOrders.map(order => <tr key={order.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-3849" data-mp="3s9cn2ljo2u71">
                      <td className="px-5 py-4 font-medium text-slate-900" data-id="element-3850" data-mp="2zjdp20nhvmq8">
                        {order.numeroPedido}
                      </td>
                      <td className="px-5 py-4 font-medium text-slate-900" data-id="element-3851" data-mp="3dg96agdwgkhd">
                        {order.farmacia}
                      </td>
                      <td className="px-5 py-4" data-id="element-3852" data-mp="srd1bd7gc4b1">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-3853" data-mp="su7j0wz2zb0r">
                          <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3854" data-mp="oq9mmu154ebe" />
                          {order.vendedor}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600" data-id="element-3855" data-mp="3mit523olnc9b">
                        {formatDate(order.fecha)}
                      </td>
                      <td className="px-5 py-4 text-center text-sm text-slate-600" data-id="element-3856" data-mp="3vksmgx3j0ics">
                        {order.items}
                      </td>
                      <td className="px-5 py-4 text-right font-medium text-slate-900" data-id="element-3857" data-mp="2yvtd1er7haj0">
                        {formatCurrency(order.total)}
                      </td>
                      <td className="px-5 py-4 text-center" data-id="element-3858" data-mp="37uol6wdt8uoy">
                        <div className="flex flex-col items-center gap-1" data-id="element-3859" data-mp="37t3rf8zblfs5">
                          {isRepartidor ? getStatusBadge(order.estado) : <button onClick={() => cycleStatus(order)} title="Clic para cambiar estado" className="cursor-pointer hover:opacity-75 transition-opacity" data-id="element-3860" data-mp="2g27lytfladew">
                              {getStatusBadge(order.estado)}
                            </button>}
                          {savedOrderId === order.id && <span className="text-xs text-emerald-600 font-medium animate-pulse" data-id="element-3861" data-mp="1ym23otd9vqkr">
                              ✓ Guardado
                            </span>}
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-3862" data-mp="1jwxl3b2kfkc4">
                        {isRepartidor ? <div className="flex items-center justify-end gap-1" data-id="element-3863" data-mp="1fwdnk6e2yepg">
                            {(order.estado === 'empacado' || order.estado === 'enviado') && <>
                                <button onClick={() => updateStatus(order, 'entregado')} title="Marcar Entregado" className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors" data-id="element-3864" data-mp="15y68jt3cq6q3">
                                  <CheckIcon className="w-3.5 h-3.5" data-id="element-3865" data-mp="2b3mbas5zyv5v" />
                                  Entregado
                                </button>
                                <button onClick={() => updateStatus(order, 'cancelado')} title="Cancelar Entrega" className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-semibold rounded-lg transition-colors" data-id="element-3866" data-mp="2fwfj5owrijm6">
                                  <BanIcon className="w-3.5 h-3.5" data-id="element-3867" data-mp="1ditjf7k2uxkd" />
                                  Cancelar
                                </button>
                              </>}
                            {order.estado === 'entregado' && <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-lg" data-id="element-3868" data-mp="1ih4pr8b5ypxu">
                                <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-3869" data-mp="1wet1nqn96mqr" />
                                Entregado
                              </span>}
                            {order.estado === 'cancelado' && <span className="text-xs text-rose-500 font-medium" data-id="element-3870" data-mp="218xakfg2x4uh">
                                Cancelado
                              </span>}
                            <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-3871" data-mp="2b6uxmzm7cvli" />
                            <button onClick={() => openEditModal(order)} title="Editar Pedido" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3872" data-mp="3ckqqk2d1i1q5">
                              <EditIcon className="w-4 h-4" data-id="element-3873" data-mp="2ylimow5xm5fm" />
                            </button>
                          </div> : isCliente ? <div className="flex items-center justify-end" data-id="element-3874" data-mp="2tnpiu9s3pnii">
                            {getStatusBadge(order.estado)}
                          </div> : <div className="flex items-center justify-end gap-1" data-id="element-3875" data-mp="2jtqgpas123qk">
                            {order.estado === 'pendiente' && <>
                                <button onClick={() => updateStatus(order, 'procesando')} title="Aprobar Pedido" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" data-id="element-3876" data-mp="2auli64gguaee">
                                  <CheckIcon className="w-4 h-4" data-id="element-3877" data-mp="ucuxpjv6zw0y" />
                                </button>
                                <button onClick={() => updateStatus(order, 'cancelado')} title="Cancelar Pedido" className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3878" data-mp="zby16zvixu6t">
                                  <BanIcon className="w-4 h-4" data-id="element-3879" data-mp="gh99hews9g9l" />
                                </button>
                                <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-3880" data-mp="3f3c6l39uh7ht" />
                              </>}
                            <button onClick={() => openEditModal(order)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3881" data-mp="r8znyzojmayo">
                              <EditIcon className="w-4 h-4" data-id="element-3882" data-mp="103xoosxxms0k" />
                            </button>
                            <button onClick={() => {
                      setEditingOrder(order);
                      setIsDeleteModalOpen(true);
                    }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-3883" data-mp="w2vly24t3xtu">
                              <TrashIcon className="w-4 h-4" data-id="element-3884" data-mp="cempvh0577p2" />
                            </button>
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </>}
      </Card>
      <div className="animate-stagger-4" data-id="element-3885" data-mp="20ab1wntsoyzt">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredOrders.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-3886" data-mp="20b2q3dl2bug2" />
      </div>
      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingOrder ? 'Editar Pedido' : 'Nuevo Pedido'} size="xl" data-id="element-3887" data-mp="2ezfkw78ez78z">
        <ModalBody data-id="element-3888" data-mp="2k03g753oea90">
          <div className="space-y-5" data-id="element-3889" data-mp="259w4d886bpha">
            {/* Header fields */}
            <div className="grid grid-cols-2 gap-4" data-id="element-3890" data-mp="257jnkye5jl5t">
              <Input label="Número de Pedido" value={headerForm.numeroPedido} onChange={e => setHeaderForm({
              ...headerForm,
              numeroPedido: e.target.value
            })} placeholder="PED-2024-001" disabled={isCliente} data-id="element-3891" data-mp="2a7fk6ckisabc" />
              {!isCliente && <div data-id="element-3892" data-mp="1a3gd8uli4i5a">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3893" data-mp="15296myv3hp07">
                    Estado
                  </label>
                  <select value={headerForm.estado} onChange={e => setHeaderForm({
                ...headerForm,
                estado: e.target.value as Order['estado']
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3894" data-mp="1047nyf1kzrp9">
                    <option value="pendiente" data-id="element-3895" data-mp="2ghxtlq8cmo10">Pendiente</option>
                    <option value="procesando" data-id="element-3896" data-mp="2gj8vtlm5bnq0">Procesando</option>
                    <option value="empacado" data-id="element-3897" data-mp="2bpxnth5ll0wi">Empacado</option>
                    <option value="enviado" data-id="element-3898" data-mp="2j2dn5ke3ijmq">Enviado</option>
                    <option value="entregado" data-id="element-3899" data-mp="2j3yihc88i6ln">Entregado</option>
                    <option value="cancelado" data-id="element-3900" data-mp="2o1zzlrm3nvuh">Cancelado</option>
                  </select>
                </div>}
            </div>
            <div className={`grid ${isCliente ? 'grid-cols-1' : 'grid-cols-3'} gap-4`} data-id="element-3901" data-mp="2t2eadhf35jw7">
              {isCliente ? <Input label="Farmacia" value={headerForm.farmacia} disabled data-id="element-3902" data-mp="321hr4eqs5n7j" /> : <SearchableSelect label="Farmacia" value={headerForm.farmacia} onChange={val => {
              const matchedClient = clients.find(c => c.nombre === val);
              let autoVendedor = headerForm.vendedor;
              if (matchedClient) {
                // Try exact name match first
                if (matchedClient.vendedorNombre && vendedores.includes(matchedClient.vendedorNombre)) {
                  autoVendedor = matchedClient.vendedorNombre;
                } else if (matchedClient.vendedorId) {
                  // Fallback: match by vendedorId to get current name from users
                  const vendedorUser = users.find(u => u.id === matchedClient.vendedorId);
                  if (vendedorUser && vendedores.includes(vendedorUser.nombre)) {
                    autoVendedor = vendedorUser.nombre;
                  }
                }
              }
              setHeaderForm({
                ...headerForm,
                farmacia: val,
                vendedor: autoVendedor
              });
            }} options={farmacias} placeholder="Buscar farmacia..." data-id="element-3903" data-mp="36z1aft2c4q6c" />}
              {!isCliente && <div data-id="element-3904" data-mp="dv0jdlwqa31i">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3905" data-mp="dth3vxiwbw0s">
                    Vendedor
                  </label>
                  <select value={headerForm.vendedor} onChange={e => setHeaderForm({
                ...headerForm,
                vendedor: e.target.value
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3906" data-mp="1jacv1o48pq">
                    <option value="" data-id="element-3907" data-mp="3i9iiujm941lz">Seleccionar vendedor...</option>
                    {vendedores.map(v => <option key={v} value={v} data-id="element-3908" data-mp="1bjaq2e7gleyy">
                        {v}
                      </option>)}
                  </select>
                </div>}
              {!isCliente && <Input label="Fecha" type="date" value={headerForm.fecha} onChange={e => setHeaderForm({
              ...headerForm,
              fecha: e.target.value
            })} data-id="element-3909" data-mp="2k2yb8v1ubbux" />}
            </div>

            {/* Error message */}
            {saveError && <div className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-3910" data-mp="21bf9erjjy3uh">
                <AlertCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-3911" data-mp="1wdnl07i48zv1" />
                <div data-id="element-3912" data-mp="1sd3nh2tmru8d">
                  <p className="font-semibold" data-id="element-3913" data-mp="1sdlenl1fri9f">Error al guardar</p>
                  <p className="mt-0.5" data-id="element-3914" data-mp="3qqddcksu610b">{saveError}</p>
                </div>
                <button onClick={() => setSaveError(null)} className="ml-auto p-1 text-rose-400 hover:text-rose-600 rounded transition-colors flex-shrink-0" data-id="element-3915" data-mp="3mq2zklysy6aw">
                  <XIcon className="w-4 h-4" data-id="element-3916" data-mp="1dgbxkdrp5aya" />
                </button>
              </div>}

            {/* Product search */}
            <div data-id="element-3917" data-mp="1i8n80ak2cf53">
              <div className="flex items-center justify-between mb-1.5" data-id="element-3918" data-mp="1ia7s168r4fop">
                <label className="block text-sm font-medium text-gray-700" data-id="element-3919" data-mp="1m8xfjrni7dm2">
                  Agregar Producto
                </label>
                <span className="text-xs text-slate-400" data-id="element-3920" data-mp="ui9eeflty88e">
                  {inventory.length} productos disponibles
                </span>
              </div>
              <div className="relative" data-id="element-3921" data-mp="yib6bx1rjnah">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3922" data-mp="13ixtldmymemv" />
                <input type="text" placeholder="Escribe para buscar o deja vacío para ver todos..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-3923" data-mp="29z9tbj5fk4kk" />
              </div>
              {productResults.length > 0 && <div className="mt-1 border border-slate-200 rounded-xl overflow-hidden shadow-sm max-h-60 overflow-y-auto" data-id="element-3924" data-mp="2v3gjro21dpgv">
                  {productResults.map(product => {
                const promo = activePromos.find(pr => pr.productId === product.id);
                return <button key={product.id} onClick={() => addProductToOrder(product)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-50 transition-colors text-left border-b border-slate-100 last:border-0" data-id="element-3925" data-mp="27ckoui2aae5e">
                        <div className="flex items-center gap-3" data-id="element-3926" data-mp="1c527oqtefs87">
                          <div className={`p-1.5 rounded-lg ${promo ? promo.tipo === 'oferta' ? 'bg-rose-100' : 'bg-amber-100' : 'bg-slate-100'}`} data-id="element-3927" data-mp="1c48xtgz58mlu">
                            <PackageIcon className={`w-4 h-4 ${promo ? promo.tipo === 'oferta' ? 'text-rose-600' : 'text-amber-600' : 'text-slate-500'}`} data-id="element-3928" data-mp="12dedii5gjx31" />
                          </div>
                          <div data-id="element-3929" data-mp="oey42jg7nsh7">
                            <div className="flex items-center gap-2" data-id="element-3930" data-mp="oeoa6ks1p168">
                              <p className="text-sm font-medium text-slate-900" data-id="element-3931" data-mp="jfl7h6zjf71f">
                                {product.nombre}
                              </p>
                              {promo && <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${promo.tipo === 'oferta' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-3932" data-mp="2dtcpouvxmz8h">
                                  {promo.tipo === 'oferta' ? '🔥 Oferta' : '⭐ Impulso'}
                                </span>}
                            </div>
                            <p className="text-xs text-slate-400" data-id="element-3933" data-mp="24potmy97jc9">
                              {product.sku} ·{' '}
                              {formatCurrency(promo && promo.precioOferta > 0 ? promo.precioOferta : product.precio)}
                              {promo && promo.descuentoPct > 0 && <span className="ml-1 text-emerald-600 font-semibold" data-id="element-3934" data-mp="30oeq308t9ubv">
                                  (-{promo.descuentoPct}%)
                                </span>}
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${product.stock === 0 ? 'bg-rose-100 text-rose-700' : product.stock <= product.stockMinimo ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-3935" data-mp="1iq00nigv94lo">
                          {product.stock === 0 ? 'Sin stock' : `${product.stock} uds`}
                        </span>
                      </button>;
              })}
                </div>}
            </div>

            {/* Line items */}
            {lineas.length > 0 && <div data-id="element-3936" data-mp="lbfe12v9mnrv">
                <div className="flex items-center justify-between mb-2" data-id="element-3937" data-mp="lavu5gm21pb7">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3938" data-mp="habyyil1kvrp">
                    Productos del Pedido
                  </label>
                  {hasStockIssues && <span className="flex items-center gap-1 text-xs font-semibold text-rose-600" data-id="element-3939" data-mp="txgmj9sauw9y">
                      <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-3940" data-mp="yw1njbfdljzg" />
                      Hay productos sin stock suficiente
                    </span>}
                </div>
                <div className="border border-slate-200 rounded-xl overflow-x-auto" data-id="element-3941" data-mp="18qadpsgxp0ug">
                  <table className="w-full text-sm min-w-[650px]" data-id="element-3942" data-mp="1dn0tsfsqnwfv">
                    <thead data-id="element-3943" data-mp="1ho46itfhpccv">
                      <tr className="bg-slate-50 border-b border-slate-200" data-id="element-3944" data-mp="1hnb1bww6iuwu">
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500" data-id="element-3945" data-mp="1mp08v2k70qg6">
                          Producto
                        </th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-slate-500" data-id="element-3946" data-mp="3oflheekqowkw">
                          Cantidad
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500" data-id="element-3947" data-mp="3tgiz6q1u7olv">
                          Precio
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500" data-id="element-3948" data-mp="3h35s745ygu8e">
                          Subtotal
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500" data-id="element-3949" data-mp="3769iiv4bm229">
                          ITBIS
                        </th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-slate-500" data-id="element-3950" data-mp="8nm40jk56207">
                          Stock
                        </th>
                        <th className="px-4 py-2.5 w-8" data-id="element-3951" data-mp="3uusqf9jgkctt"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-3952" data-mp="3pvpjfjjx3wj7">
                      {lineas.map(linea => {
                    const indicator = getStockIndicator(linea);
                    return <tr key={linea.productoId} className={linea.cantidad > linea.stockDisponible ? 'bg-rose-50/30' : ''} data-id="element-3953" data-mp="2eg0wgs6nyxie">
                            <td className="px-4 py-3" data-id="element-3954" data-mp="11itra8y1szfr">
                              <p className="font-medium text-slate-900 text-xs" data-id="element-3955" data-mp="11h8ueco9h4eq">
                                {linea.nombre}
                              </p>
                              <p className="text-xs text-slate-400" data-id="element-3956" data-mp="rl5piz20yro3">
                                {linea.sku}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-3957" data-mp="isc1ac4lohy3">
                              <input type="number" min="1" value={linea.cantidad} onChange={e => updateLineCantidad(linea.productoId, Number(e.target.value))} className="w-16 text-center border border-slate-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3958" data-mp="iskbfw9c995l" />
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700" data-id="element-3959" data-mp="85dzyz27fn69">
                              {formatCurrency(linea.precio)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900" data-id="element-3960" data-mp="hvyx0i28cjn2">
                              {formatCurrency(linea.cantidad * linea.precio)}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600" data-id="element-3961" data-mp="rvzia9jnt9tw">
                              {formatCurrency(linea.cantidad * (linea.itbis || 0))}
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-3962" data-mp="38nqb3v8du65p">
                              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${indicator.bg} ${indicator.color}`} data-id="element-3963" data-mp="38qcedio8pzi4">
                                {indicator.icon} {indicator.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-3964" data-mp="2pyjovy8dqwf5">
                              <button onClick={() => removeLinea(linea.productoId)} className="p-1 text-slate-400 hover:text-rose-600 rounded transition-colors" data-id="element-3965" data-mp="2uwbf8ntdmah8">
                                <XIcon className="w-3.5 h-3.5" data-id="element-3966" data-mp="227xischxrm7s" />
                              </button>
                            </td>
                          </tr>;
                  })}
                    </tbody>
                    <tfoot data-id="element-3967" data-mp="ka14exhbbf9k">
                      <tr className="bg-slate-50 border-t border-slate-200" data-id="element-3968" data-mp="kbvqwg88fffr">
                        <td colSpan={3} className="px-4 py-3 text-sm font-semibold text-slate-700" data-id="element-3969" data-mp="fay6ry3nwbbm">
                          Total ({lineas.reduce((s, l) => s + l.cantidad, 0)}{' '}
                          unidades)
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-900" data-id="element-3970" data-mp="ls23um21swc">
                          {formatCurrency(orderSubtotal)}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-700" data-id="element-3971" data-mp="3ns2i3n9gnk7n">
                          {formatCurrency(orderItbisTotal)}
                        </td>
                        <td colSpan={2} data-id="element-3972" data-mp="3dt3h8quxssv7"></td>
                      </tr>
                      <tr className="bg-blue-50 border-t border-blue-200" data-id="element-3973" data-mp="3dtvcg0kmgoky">
                        <td colSpan={4} className="px-4 py-3 text-sm font-bold text-blue-800 text-right" data-id="element-3974" data-mp="2nngv2q0st94w">
                          Total con ITBIS
                        </td>
                        <td className="px-4 py-3 text-right font-black text-blue-900 text-base" data-id="element-3975" data-mp="2rqn05uc6cu9s">
                          {formatCurrency(orderTotal)}
                        </td>
                        <td colSpan={2} data-id="element-3976" data-mp="23yg99ap7xfhr"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>}

            {lineas.length === 0 && <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-slate-200 rounded-xl text-slate-400" data-id="element-3977" data-mp="2905mmwzwy590">
                <PackageIcon className="w-8 h-8 mb-2" data-id="element-3978" data-mp="1l904ucaex2w2" />
                <p className="text-sm font-medium" data-id="element-3979" data-mp="1gbhuchgqyfly">
                  Busca y agrega productos al pedido
                </p>
              </div>}
          </div>
        </ModalBody>
        <ModalFooter data-id="element-3980" data-mp="jw50auelymk">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-3981" data-mp="k5wk2v3kdud">
            Cancelar
          </Button>
          {lineas.length > 0 && <Button variant="secondary" leftIcon={<PrinterIcon className="w-4 h-4" data-id="element-3983" data-mp="3nxrrf6j8o7o1" />} onClick={async () => {
          // Only save cotización to DB if not already saved in this session
          if (!savedCotizacionId) {
            try {
              const totalItems = lineas.reduce((sum, l) => sum + l.cantidad, 0);
              const cotizacionPayload = {
                numero_cotizacion: headerForm.numeroPedido,
                farmacia: headerForm.farmacia,
                vendedor: headerForm.vendedor,
                fecha: new Date(headerForm.fecha).toISOString(),
                items: totalItems,
                total: orderTotal
              };
              const {
                data: newCotizacion,
                error: cotError
              } = await supabaseAdmin.from('cotizaciones').insert(cotizacionPayload).select().single();
              if (cotError) {
                console.error('Error guardando cotización:', cotError.message, cotError.details, cotError.hint);
              }
              if (newCotizacion) {
                setSavedCotizacionId(newCotizacion.id);
                // Save cotización lines
                if (lineas.length > 0) {
                  const printCotLinesPayload = lineas.map(l => ({
                    cotizacion_id: newCotizacion.id,
                    producto_id: l.productoId,
                    nombre: l.nombre,
                    sku: l.sku,
                    cantidad: l.cantidad,
                    precio: l.precio,
                    stock_disponible: l.stockDisponible
                  }));
                  const {
                    error: linesError
                  } = await supabaseAdmin.from('cotizacion_lines').insert(printCotLinesPayload).select();
                  if (linesError) {
                    console.error('Error guardando líneas de cotización (print):', linesError.message);
                  }
                }
              }
            } catch (err) {
              console.error('Error al guardar cotización en BD:', err);
            }
          }
          // Generate PDF (always, even if DB save fails or was already saved)
          const matchedClient = clients.find(c => c.nombre === headerForm.farmacia);
          await generateCotizacionPDF({
            numeroPedido: headerForm.numeroPedido,
            farmacia: headerForm.farmacia,
            vendedor: headerForm.vendedor,
            fecha: headerForm.fecha,
            lineas,
            clientData: matchedClient || null
          });
        }} data-id="element-3982" data-mp="3ivso1wni1ar3">
              Imprimir Cotización
            </Button>}
          <Button onClick={handleSave} isLoading={isSaving} disabled={!headerForm.numeroPedido || lineas.length === 0} data-id="element-3984" data-mp="1hxqodcaxop58">
            {editingOrder ? 'Guardar Cambios' : 'Crear Pedido'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Pedido" size="sm" data-id="element-3985" data-mp="1msxqifok1ls1">
        <ModalBody data-id="element-3986" data-mp="2agufqb79brgw">
          <p className="text-slate-600" data-id="element-3987" data-mp="2ag1ko7hnuqel">
            ¿Eliminar el pedido <strong data-id="element-3988" data-mp="1whbc9vpe54ax">{editingOrder?.numeroPedido}</strong>?
            Esta acción no se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-3989" data-mp="21jk7kypqkig7">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-3990" data-mp="1msjps333nc7e">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-3991" data-mp="1rkuo51i6ypxu">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}